// 리스트 DataGrid 행(row) 클릭 시 해당 리스트 자신의 도메인 상세페이지로 이동시키는 훅.
// DataGridPanel이 내부에서 사용하지만, 예외가 필요한 도메인은 이 훅을 직접 써도 된다
// (공용 prop을 비대화시키지 않는 탈출구 — .omc/plans/ralplan-list-row-navigation.md 참고).

import { useNavigate } from "react-router-dom";
import type { GridEventListener } from "@mui/x-data-grid";

export function useRowShowNavigation<R>(
  getRowShowPath?: (row: R) => string | null | undefined
) {
  const navigate = useNavigate();

  if (!getRowShowPath) return { onRowClick: undefined, sx: false as const };

  const onRowClick: GridEventListener<"rowClick"> = (params, event) => {
    // MUI X v6 옵트아웃 규약은 defaultMuiPrevented (defaultPrevented 아님).
    if (event.defaultMuiPrevented) return;

    const target = event.target as HTMLElement;
    // 일반 컬럼(type:"actions" 미지정) 안의 버튼/링크/체크박스 클릭은 무시.
    // 체크박스/type:"actions" 컬럼 자체는 MUI가 이미 rowClick을 발행하지 않지만,
    // 이 리포는 actions 컬럼에 type을 안 붙이고 field만 쓰므로 이 가드가 유일한 방어선.
    if (target.closest('button, a, input, [role="checkbox"]')) return;

    // 텍스트 드래그로 셀 내용을 복사하던 중이면 무시(mouseup이 rowClick으로 이어짐).
    if (window.getSelection()?.toString()) return;

    const path = getRowShowPath(params.row);
    if (!path) return;

    // Ctrl/Cmd 클릭은 새 탭으로 — 기존 <a> 태그가 제공하던 브라우저 기본 동작 보존.
    // 휠클릭(middle-click)은 다루지 않는다 — MUI DataGrid 행은 onClick만 바인딩해
    // auxclick을 못 받으므로 재현 불가능한 죽은 분기가 된다.
    if (event.ctrlKey || event.metaKey) {
      window.open(path, "_blank", "noopener");
      return;
    }

    navigate(path);
  };

  return { onRowClick, sx: { "& .MuiDataGrid-row": { cursor: "pointer" } } };
}
